import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, formatPrice } from "@/lib/api";
import { Plus, Save, Trash2, X } from "lucide-react";

const empty = {
  slug: "", title: "", category: "career_advisory", tagline: "", description: "",
  features: [], duration_minutes: 60, price_inr: 0, price_usd: 0, is_bookable: true, display_order: 100,
};

export function AdminServices() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(empty); };
  const openEdit = (s) => {
    setEditing(s.id);
    setForm({ ...empty, ...s });
  };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, features: typeof form.features === "string" ? form.features.split("\n").filter(Boolean) : form.features };
      if (editing === "new") await api.post("/services", body);
      else await api.put(`/services/${editing}`, body);
      toast.success("Saved.");
      close();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/services/${id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-services-page">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="eyebrow">Catalogue</div>
          <h1 className="font-display text-4xl mt-3 tracking-tight">Services & Pricing</h1>
        </div>
        <button className="btn-primary" onClick={openNew} data-testid="admin-service-new"><Plus size={14} /> New service</button>
      </div>

      <div className="border border-[#E0DCD3] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate2 font-mono uppercase text-[10px] tracking-[0.24em] border-b border-[#E0DCD3]">
              <th className="p-4">Title</th><th className="p-4">Category</th><th className="p-4">INR</th><th className="p-4">USD</th><th className="p-4">Bookable</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id} className="border-b border-[#E0DCD3] last:border-0">
                <td className="p-4"><div className="font-medium">{s.title}</div><div className="text-xs text-slate2">{s.slug}</div></td>
                <td className="p-4">{s.category}</td>
                <td className="p-4">{formatPrice("INR", s.price_inr)}</td>
                <td className="p-4">{formatPrice("USD", s.price_usd)}</td>
                <td className="p-4">{s.is_bookable ? "Yes" : "No"}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(s)} className="editorial-link mr-4" data-testid={`admin-service-edit-${s.slug}`}>Edit</button>
                  <button onClick={() => remove(s.id)} className="text-red-700 hover:underline" data-testid={`admin-service-delete-${s.slug}`}><Trash2 size={14} className="inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-charcoal/40 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-ivory border border-[#E0DCD3] max-w-2xl w-full p-8 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-service-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl">{editing === "new" ? "New service" : "Edit service"}</h2>
              <button onClick={close}><X /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><label className="eyebrow eyebrow-slate">Slug</label><input className="input-flush mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Title</label><input className="input-flush mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Category</label>
                <select className="input-flush mt-1" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  <option value="career_advisory">career_advisory</option>
                  <option value="enterprise">enterprise</option>
                </select>
              </div>
              <div><label className="eyebrow eyebrow-slate">Duration (min)</label><input type="number" className="input-flush mt-1" value={form.duration_minutes ?? 0} onChange={(e) => setForm({ ...form, duration_minutes: +e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Price INR</label><input type="number" className="input-flush mt-1" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: +e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Price USD</label><input type="number" className="input-flush mt-1" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: +e.target.value })} /></div>
              <div className="md:col-span-2"><label className="eyebrow eyebrow-slate">Tagline</label><input className="input-flush mt-1" value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="eyebrow eyebrow-slate">Description</label><textarea rows={4} className="input-flush mt-1 resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="md:col-span-2"><label className="eyebrow eyebrow-slate">Features (one per line)</label><textarea rows={4} className="input-flush mt-1 resize-none" value={Array.isArray(form.features) ? form.features.join("\n") : form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Display order</label><input type="number" className="input-flush mt-1" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: +e.target.value })} /></div>
              <div className="flex items-center gap-2"><input id="bkbl" type="checkbox" checked={form.is_bookable} onChange={(e) => setForm({ ...form, is_bookable: e.target.checked })} /><label htmlFor="bkbl" className="text-sm">Bookable</label></div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button className="btn-outline" onClick={close}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={busy} data-testid="admin-service-save"><Save size={14} /> {busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminBookings() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}`, { status }); toast.success("Updated."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-bookings-page">
      <div className="eyebrow">Diary</div>
      <h1 className="font-display text-4xl mt-3 tracking-tight">Appointment requests</h1>
      <div className="mt-8 border border-[#E0DCD3] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate2 font-mono uppercase text-[10px] tracking-[0.24em] border-b border-[#E0DCD3]">
              <th className="p-4">Client</th><th className="p-4">Service</th><th className="p-4">Preferred slot</th><th className="p-4">Status</th><th className="p-4">CV</th><th className="p-4">Notes</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-[#E0DCD3] last:border-0">
                <td className="p-4"><div className="font-medium">{b.client_name}</div><div className="text-xs text-slate2">{b.client_email} · {b.client_phone}</div>{b.client_role && <div className="text-xs text-slate2 mt-1">{b.client_role}</div>}</td>
                <td className="p-4">{b.service_title}</td>
                <td className="p-4">{new Date(b.slot_iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</td>
                <td className="p-4">
                  <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} className="border border-[#E0DCD3] px-2 py-1 text-xs bg-white">
                    <option value="requested">Requested</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="p-4">{b.cv_file_id ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/cv/${b.cv_file_id}`} target="_blank" rel="noreferrer" className="editorial-link">Download</a> : "—"}</td>
                <td className="p-4 max-w-xs"><div className="text-xs text-slate2 whitespace-pre-wrap line-clamp-3">{b.notes || "—"}</div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate2">No requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminLeads() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/contact").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="admin-leads-page">
      <div className="eyebrow">Enquiries</div>
      <h1 className="font-display text-4xl mt-3 tracking-tight">Leads</h1>
      <div className="mt-8 space-y-4">
        {items.map((l) => (
          <div key={l.id} className="border border-[#E0DCD3] bg-white p-6">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="font-display text-xl">{l.name} <span className="text-slate2 text-sm">· {l.email}</span></div>
                {l.company && <div className="text-sm text-slate2">{l.company} · {l.phone || "—"}</div>}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate2">{new Date(l.created_at).toLocaleString()}</div>
            </div>
            {l.subject && <div className="mt-3 eyebrow eyebrow-slate">{l.subject}</div>}
            <p className="mt-2 text-slate2 whitespace-pre-wrap">{l.message}</p>
          </div>
        ))}
        {items.length === 0 && <div className="text-slate2">No enquiries yet.</div>}
      </div>
    </div>
  );
}

export function AdminSubscribers() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/newsletter").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Remove?")) return; try { await api.delete(`/newsletter/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <div data-testid="admin-subscribers-page">
      <div className="eyebrow">Newsletter</div>
      <h1 className="font-display text-4xl mt-3 tracking-tight">Subscribers</h1>
      <div className="mt-8 border border-[#E0DCD3] bg-white">
        {items.map((i) => (
          <div key={i.id} className="p-4 border-b border-[#E0DCD3] last:border-0 flex items-center justify-between">
            <div><div className="font-medium">{i.email}</div><div className="text-xs text-slate2">via {i.source} · {new Date(i.created_at).toLocaleDateString()}</div></div>
            <button onClick={() => remove(i.id)} className="text-red-700 text-sm hover:underline"><Trash2 size={14} className="inline" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center text-slate2">No subscribers yet.</div>}
      </div>
    </div>
  );
}

const emptyPost = { slug: "", title: "", excerpt: "", body_html: "", cover_image: "", tags: [], author: "Roy Mathew", is_published: true };

export function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/blog", { params: { include_unpublished: true } }).then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(emptyPost); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...emptyPost, ...p }); };
  const close = () => setEditing(null);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
      if (editing === "new") await api.post("/blog", body);
      else await api.put(`/blog/${editing}`, body);
      toast.success("Saved."); close(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/blog/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-baseline justify-between mb-8">
        <div><div className="eyebrow">Journal</div><h1 className="font-display text-4xl mt-3 tracking-tight">Posts</h1></div>
        <button className="btn-primary" onClick={openNew} data-testid="admin-blog-new"><Plus size={14} /> New post</button>
      </div>

      <div className="border border-[#E0DCD3] bg-white">
        {posts.map((p) => (
          <div key={p.id} className="p-6 border-b border-[#E0DCD3] last:border-0 flex items-center justify-between gap-4">
            <div>
              <div className="font-display text-2xl leading-tight">{p.title}</div>
              <div className="text-xs text-slate2 mt-1">{p.slug} · {p.is_published ? "Published" : "Draft"}</div>
            </div>
            <div className="flex gap-3">
              <button className="editorial-link" onClick={() => openEdit(p)}>Edit</button>
              <button className="text-red-700 text-sm" onClick={() => remove(p.id)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {posts.length === 0 && <div className="p-8 text-center text-slate2">No posts yet.</div>}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 bg-charcoal/40 z-40 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-ivory border border-[#E0DCD3] max-w-2xl w-full p-8 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-blog-form">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-3xl">{editing === "new" ? "New post" : "Edit post"}</h2>
              <button onClick={close}><X /></button>
            </div>
            <div className="space-y-6">
              <div><label className="eyebrow eyebrow-slate">Slug</label><input className="input-flush mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Title</label><input className="input-flush mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Excerpt</label><textarea rows={3} className="input-flush mt-1 resize-none" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Body (HTML)</label><textarea rows={10} className="input-flush mt-1 resize-none font-mono text-xs" value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Cover image URL</label><input className="input-flush mt-1" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} /></div>
              <div><label className="eyebrow eyebrow-slate">Tags (comma separated)</label><input className="input-flush mt-1" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} /></div>
              <div className="flex items-center gap-2"><input id="pub" type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} /><label htmlFor="pub" className="text-sm">Published</label></div>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button className="btn-outline" onClick={close}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={busy} data-testid="admin-blog-save"><Save size={14} /> {busy ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
